import axios from "axios";
import {getAccessToken} from "./SessionContext";

// The Customer portal's API client. One place that knows the tenant prefix, so a screen never builds
// a path by hand and cannot accidentally address the other portal's endpoints.

const BASE = process.env.REACT_APP_API_URL ?? "";
const TENANT = "t8";

export type ApiResult = {status: number; data: any};

// Never throws on a non-2xx: every auth response is meaningful (400 is a wrong password, 429 is a
// lockout), so the caller reads status and Code rather than catching.
/**
 * RAISED WHEN THE GATEWAY REFUSES A SIGNED-IN REQUEST -- the session has ended (an access token lives an hour). App
 * listens and signs out with a sentence saying why. Before this the refusal surfaced as its raw code (CC-VEGN-JV9K), and a
 * section whose list was refused stayed in the menu because a list that fails keeps its section to explain itself.
 */
export const SESSION_ENDED = "customerPortal:session-ended";

async function request(method: "GET" | "POST", path: string, body?: any, headers?: any): Promise<ApiResult> {
    try {
        const response = await axios({
            method,
            baseURL: BASE,
            url: `${TENANT}/v1/${path}`,
            headers: headers ?? {},
            data: body,
        });
        return {status: response.status, data: response.data};
    } catch (error: any) {
        if (error?.response) {
            const status = error.response.status;
            if ((status === 401 || status === 403) && headers?.["X-Cres-AccessToken"] && !path.startsWith("authenticate")) {
                window.dispatchEvent(new Event(SESSION_ENDED));
            }
            return {status, data: error.response.data};
        }
        // A transport failure has no Code, so screens fall through to their generic message.
        return {status: 0, data: null};
    }
}

// The sign-in POST is itself authorized: this token is what keeps the endpoint from being an open
// door for credential stuffing.
export async function preauthToken(): Promise<string | null> {
    const result = await request("GET", "authenticate?Type=preauth");
    return result.status === 200 ? (result.data?.Token ?? null) : null;
}

export async function signIn(email: string, password: string): Promise<ApiResult> {
    const token = await preauthToken();
    if (!token) return {status: 0, data: null};
    return request("POST", "authenticate", {Email: email, Password: password}, {"X-Cres-AccessToken": token});
}

export async function selectAccount(selectionToken: string, accountUSID: string): Promise<ApiResult> {
    // The selection token goes in the SAME header the preauth token used. This method is
    // CUSTOM-authorized, so a request without X-Cres-AccessToken is rejected at the gateway before it
    // reaches the handler; the token carries a paths claim naming exactly this endpoint.
    return request("POST", "authenticate",
        {AccountUSID: accountUSID},
        {"X-Cres-AccessToken": selectionToken});
}

export async function sendResetCode(email: string): Promise<ApiResult> {
    return request("POST", "forgot_password_send_code", {Email: email});
}

export async function verifyResetCode(key: string, code: string): Promise<ApiResult> {
    return request("POST", "forgot_password_verify_code", {Key: key, Code: code});
}

export async function setNewPassword(key: string, code: string, newPassword: string): Promise<ApiResult> {
    return request("POST", "forgot_password", {Key: key, Code: code, NewPassword: newPassword});
}

// The forced first password change, for a contact signing in on an issued password. The temp token
// from CC-T8PW-INIT is the authorisation -- it is scoped to this endpoint alone -- and a successful
// call returns a full session, so the caller lands signed in rather than back at the form.
export async function changeInitialPassword(tempToken: string, newPassword: string): Promise<ApiResult> {
    return request("POST", "authenticate_change_password",
        {NewPassword: newPassword},
        {"X-Cres-AccessToken": tempToken});
}


// ─────────────────────────────────────────────────────────────
// The documents published to the person signed in.
//
// EVERY ONE OF THESE IS AUTHORISED BY THE SESSION TOKEN AND NOTHING ELSE. There is no recipient
// parameter anywhere below, deliberately: the service reads the external identity the authorizer
// resolved, so a caller cannot ask what somebody else was given. Adding one here would be the
// hole, and it is the obvious shape to reach for.
// ─────────────────────────────────────────────────────────────

export type PublishedDocument = {
    USID: string;
    SubjectUSID: string;
    Binding: string;
    Label: string | null;
    From: string | null;
    Format: string | null;
    ComponentCount: number | null;
    IngestedDatetime: string | null;
    LastScannedDatetime: string | null;
    ExpiresDate: string | null;
    GrantedDatetime?: string | null;
    SpecVersion?: string | null;
    Quality?: Record<string, any>;
    Vulnerabilities?: number;
    Unassessed?: number;
};

export type PublishedComponent = {
    USID: string;
    Name: string | null;
    Version: string | null;
    Purl: string | null;
    Cpe: string | null;
    Licence: string | null;
    Supplier: string | null;
    Type: string | null;
};

function authorised() {
    const token = getAccessToken();
    return token ? {"X-Cres-AccessToken": token} : {};
}

export async function listPublishedDocuments(): Promise<ApiResult> {
    return request("GET", "sbom_documents", undefined, authorised());
}

export async function readPublishedDocument(usid: string): Promise<ApiResult> {
    return request("GET", `sbom_documents?USID=${encodeURIComponent(usid)}`, undefined, authorised());
}

export async function listPublishedComponents(usid: string): Promise<ApiResult> {
    return request("GET", `sbom_components?USID=${encodeURIComponent(usid)}`, undefined, authorised());
}

export type DownloadFormat = "cyclonedx" | "spdx" | "vex";

/**
 * A short-lived URL to one published document, in one format.
 *
 * A URL AND NOT THE BYTES. The service writes to S3 and presigns; nothing streams through a
 * Lambda. The caller follows the URL itself, which is also what makes the browser save the file
 * under a sensible name rather than render JSON in a tab.
 */
export async function downloadPublished(usid: string, format: DownloadFormat): Promise<ApiResult> {
    return request("GET",
        `sbom_downloads?USID=${encodeURIComponent(usid)}&Format=${format}`,
        undefined, authorised());
}


// ─────────────────────────────────────────────────────────────
// The lots of material released to the person signed in.
//
// THE SAME RULE AS THE BLOCK ABOVE AND FOR THE SAME REASON: no recipient parameter, anywhere. The
// service reads the external identity the authorizer resolved, so a caller cannot ask what
// somebody else was given, and adding a parameter here would be the hole.
// ─────────────────────────────────────────────────────────────

export type ReleasedLot = {
    USID: string;
    LotObjectUSID: string;
    // The number printed on the box. A recipient knows the material by this and by nothing else.
    LotNumber: string | null;
    Name: string | null;
    From: string | null;
    Quantity: number | string | null;
    UnitOfMeasure: string | null;
    ShipmentReference: string | null;
    ShippedDatetime: string | null;
    ReleasedDatetime: string | null;
    ExpiresDatetime: string | null;
    // THE COUNT, where the detail below carries the array under `Documents`. Two names rather
    // than one field that is a number in one response and a list in another, which is the shape
    // a client ends up type-guarding at every call site.
    DocumentCount: number;
    HasGenealogy: boolean;
};

export type ReleasedDocument = {
    AttachmentUSID: string;
    Kind: string | null;
    Name: string | null;
    Extension: string | null;
    MimeType: string | null;
    Bytes: number | null;
    // The record says this file shipped and the bytes are gone. Listed rather than hidden, so the
    // fact that it was provided is not lost along with the file.
    Missing: boolean;
};

export type GenealogyEntry = {
    ObjectUSID: string;
    ObjectID: string | null;
    Name: string | null;
    Type: string | null;
    Generation: number;
    WentInto: string;
};

export type ReleasedLotDetail = ReleasedLot & {
    Item: {ObjectID: string | null; Name: string | null} | null;
    Documents: ReleasedDocument[];
    DocumentsAreAsReleased: boolean;
    Genealogy: GenealogyEntry[];
    // THE DIFFERENCE BETWEEN "nothing went into this" AND "you were not shown what did". An empty
    // Genealogy means both, and a reader looking at an empty panel would take the first.
    GenealogyDisclosed: boolean;
};

export async function listReleasedLots(): Promise<ApiResult> {
    return request("GET", "lots", undefined, authorised());
}

export async function readReleasedLot(usid: string): Promise<ApiResult> {
    return request("GET", `lots?USID=${encodeURIComponent(usid)}`, undefined, authorised());
}

/**
 * A short-lived URL to one certificate.
 *
 * A URL AND NOT THE BYTES, as with the SBOM downloads above: the service presigns and the caller
 * follows it, which is also what makes the browser save the file under its own name rather than
 * render it in a tab.
 */
export async function downloadLotCertificate(usid: string,
                                             attachmentUsid: string): Promise<ApiResult> {
    return request("GET",
        `lot_certificates?USID=${encodeURIComponent(usid)}`
        + `&AttachmentUSID=${encodeURIComponent(attachmentUsid)}`,
        undefined, authorised());
}

// ─────────────────────────────────────────────────────────────
// Folders, projects and documents shared with the person signed in (t1's customer shares). No recipient parameter,
// as everywhere above: the service reads the identity the authorizer resolved.
// ─────────────────────────────────────────────────────────────

/** "Doc" is a standalone Parkour Doc (in a folder, belonging to no record); it is listed under Parkour-AI Documents. */
export type ShareKind = "Folder" | "Project" | "Document" | "Doc";

export type Share = {
    USID: string;
    Kind: ShareKind;
    Number: string | null;
    Name: string;
    Note: string | null;
    From: string | null;
    SharedDatetime: string | null;
    ExpiresDatetime: string | null;
    /** What this share's level allows beyond reading it. Absent from a server older than 2026-11-04: treat as allowed. */
    May?: {Download: boolean; Render: boolean; Suggest?: boolean};
    /** An open draft this contact may review -- offered only to a share at the Review level. */
    Draft?: {Revision: string | null} | null;
};

export type SharedFile = {
    AttachmentUSID: string;
    Name: string;
    Extension: string | null;
    Bytes: number | null;
    Missing: boolean;
};

export type SharedRecord = {
    ObjectUSID: string;
    Number: string | null;
    Name: string;
    Description: string | null;
    Kind: string;
    Revision: string | null;
    ReleasedDatetime: string | null;
    EffectiveDatetime: string | null;
    Files: SharedFile[];
};

export type ShareDetail = Share & {
    Folder?: {FolderUSID: string; Name: string; Description: string | null};
    Path?: {FolderUSID: string; Name: string}[];
    Subfolders?: {FolderUSID: string; Name: string}[];
    Project?: {ObjectUSID: string; Number: string | null; Name: string; Description: string | null; Status: string | null};
    Document?: SharedRecord;
    Doc?: {USID: string; Name: string};
    /** A folder share: the standalone Parkour Docs filed in the folder being read (2026-11-04). */
    Docs?: {USID: string; Name: string}[];
    Records?: SharedRecord[];
};

export async function listShares(kind: ShareKind): Promise<ApiResult> {
    return request("GET", `shares?Kind=${kind}`, undefined, authorised());
}

export async function readShare(usid: string, folderUsid?: string): Promise<ApiResult> {
    const folder = folderUsid ? `&FolderUSID=${encodeURIComponent(folderUsid)}` : "";
    return request("GET", `shares?USID=${encodeURIComponent(usid)}${folder}`, undefined, authorised());
}

export async function downloadSharedFile(usid: string, objectUsid: string, attachmentUsid: string): Promise<ApiResult> {
    return request("GET", `share_files?USID=${encodeURIComponent(usid)}&ObjectUSID=${encodeURIComponent(objectUsid)}`
        + `&AttachmentUSID=${encodeURIComponent(attachmentUsid)}`, undefined, authorised());
}

/** The address that opens a shared document in Parkour-AI Docs, with access to that one document. */
export async function docsLinkForShare(usid: string, draft = false, docUsid?: string): Promise<ApiResult> {
    // WITH docUsid, a standalone Doc inside a shared folder; the server checks it is inside.
    const doc = docUsid ? `&DocUSID=${encodeURIComponent(docUsid)}` : "";
    return request("GET", `share_docs_links?USID=${encodeURIComponent(usid)}${draft ? "&Draft=true" : ""}${doc}`, undefined, authorised());
}
